import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Bell } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, fmtDate, formatApiErrorDetail } from "@/lib/api";
import { useSyncedReload } from "@/hooks/useSyncedReload";
import { useLang } from "@/context/LanguageContext";

// Same activity types as the global Diary page (translations already exist).
const ENTRY_TYPES = ["note", "call", "email", "meeting", "viewing", "appointment"];

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Timeline of dated notes for a single record (buyer/seller/property).
 *
 * Reads/writes to the `diary_entries` collection filtered by the appropriate
 * linked_* id. Follow-up toggle + date still auto-generates a Task via the
 * existing `sync_next_action_task` hook on the backend, so nothing changes
 * about how reminders work — only where the user enters the note.
 */
export default function Timeline({ linkKey, linkId, linkName }) {
  // linkKey: "buyer_id" | "seller_id" | "property_id"
  const { t } = useLang();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [entryType, setEntryType] = useState("note");
  const [entryDate, setEntryDate] = useState(today());
  const [followUp, setFollowUp] = useState(false);
  const [followUpDate, setFollowUpDate] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!linkId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/diary_entries?${linkKey}=${linkId}`);
      // Newest first
      const sorted = [...data].sort((a, b) => (b.date_time || "").localeCompare(a.date_time || ""));
      setEntries(sorted);
    } finally {
      setLoading(false);
    }
  }, [linkKey, linkId]);

  useEffect(() => { load(); }, [load]);
  useSyncedReload(load);

  const addNote = async () => {
    if (!body.trim()) return toast.error(t("noteBodyRequired"));
    setSaving(true);
    // Map link key to the trio of link fields the backend expects
    const linkFields = {};
    if (linkKey === "buyer_id") { linkFields.buyer_id = linkId; linkFields.buyer_name = linkName || ""; }
    if (linkKey === "seller_id") { linkFields.seller_id = linkId; linkFields.seller_name = linkName || ""; }
    if (linkKey === "property_id") { linkFields.property_id = linkId; linkFields.property_name = linkName || ""; }
    if (linkKey === "lead_id") { linkFields.lead_id = linkId; linkFields.lead_name = linkName || ""; }
    try {
      // Keep the current time-of-day so several entries logged the same day
      // still sort chronologically, while honouring the chosen date.
      const dateTime = `${entryDate || today()}T${new Date().toISOString().slice(11)}`;
      await api.post("/diary_entries", {
        date_time: dateTime,
        entry_type: entryType,
        subject: subject.trim(),
        body: body.trim(),
        follow_up_required: followUp,
        follow_up_date: followUp ? followUpDate : "",
        ...linkFields,
      });
      setSubject(""); setBody(""); setFollowUp(false); setFollowUpDate("");
      setEntryType("note"); setEntryDate(today());
      toast.success(t("noteAdded"));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await api.delete(`/diary_entries/${id}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div data-testid="record-timeline" className="space-y-5">
      {/* Compose new note */}
      <div className="bg-stone-50 border border-stone-200 rounded-lg p-4 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="min-w-0">
            <Select value={entryType} onValueChange={setEntryType}>
              <SelectTrigger data-testid="timeline-type-select" className="bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ENTRY_TYPES.map((k) => (
                  <SelectItem key={k} value={k}>{t(`diaryType_${k}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-0">
            <Input
              type="date"
              data-testid="timeline-date-input"
              className="bg-white"
              value={entryDate}
              onChange={(e) => setEntryDate(e.target.value)}
            />
          </div>
        </div>
        <Input
          data-testid="timeline-subject-input"
          placeholder={t("noteSubjectPlaceholder")}
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />
        <textarea
          data-testid="timeline-body-input"
          placeholder={t("noteBodyPlaceholder")}
          rows={3}
          className="w-full text-sm bg-white border border-stone-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-stone-900/10 focus:border-stone-400"
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <div className="flex items-center flex-wrap gap-3 justify-between">
          <label className="flex items-center gap-2 text-xs text-stone-600 cursor-pointer">
            <Checkbox
              data-testid="timeline-followup-checkbox"
              checked={followUp}
              onCheckedChange={(c) => setFollowUp(!!c)}
            />
            <Bell size={12} strokeWidth={1.5} /> {t("followUpRequired")}
          </label>
          {followUp && (
            <Input
              type="date"
              data-testid="timeline-followup-date"
              className="h-8 w-[152px] text-xs"
              value={followUpDate}
              onChange={(e) => setFollowUpDate(e.target.value)}
            />
          )}
          <button
            data-testid="timeline-add-note"
            onClick={addNote}
            disabled={saving}
            className="ml-auto inline-flex items-center gap-1.5 bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-60"
          >
            <Plus size={13} strokeWidth={1.5} /> {t("addNote")}
          </button>
        </div>
      </div>

      {/* Existing entries */}
      {loading && <p className="text-xs text-stone-400">…</p>}
      {!loading && entries.length === 0 && (
        <p className="text-sm text-stone-400 italic">{t("timelineEmpty")}</p>
      )}
      <ul className="space-y-3">
        {entries.map((e) => (
          <li
            key={e.id}
            data-testid={`timeline-entry-${e.id}`}
            className="group bg-white border border-stone-200 rounded-lg px-4 py-3"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-[11px] uppercase tracking-wide text-stone-400 flex items-center gap-2">
                  {fmtDate(e.date_time) || fmtDate(e.created_at)}
                  {e.entry_type && (
                    <span className="normal-case tracking-normal inline-block px-1.5 py-0.5 text-[10px] font-medium bg-stone-100 text-stone-600 rounded" data-testid={`timeline-entry-type-${e.id}`}>
                      {t(`diaryType_${e.entry_type}`)}
                    </span>
                  )}
                </p>
                {e.subject && (
                  <p className="text-sm font-medium text-stone-900 mt-1">{e.subject}</p>
                )}
                {e.body && (
                  <p className="text-sm text-stone-700 whitespace-pre-line mt-1">{e.body}</p>
                )}
                {e.follow_up_required && e.follow_up_date && (
                  <p className="text-[11px] text-amber-700 mt-2 inline-flex items-center gap-1">
                    <Bell size={11} strokeWidth={1.5} />
                    {t("followUpOn")} {fmtDate(e.follow_up_date)}
                  </p>
                )}
              </div>
              <button
                data-testid={`timeline-delete-${e.id}`}
                onClick={() => remove(e.id)}
                className="opacity-0 group-hover:opacity-100 p-1 text-stone-300 hover:text-red-600 rounded transition-opacity"
              >
                <Trash2 size={13} strokeWidth={1.5} />
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
